import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { merge } from 'lodash';

import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container/Container';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import createStyles from '@material-ui/core/styles/createStyles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import Close from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import Button from '@material-ui/core/Button';

import Background from 'components/Layout/Background';

import { RootStore } from 'stores/rootStore';
import { EdwardColors } from 'enums';
import { AdminUserPortfoliosStore } from 'stores/admin/adminUserPortfolios';

interface Stores {
  rootStore: RootStore;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    portfolio: {
      borderBottom: 'solid #eee 2px',
      marginBottom: theme.spacing(2),
      '& button': {
        margin: theme.spacing(0, 1),
      },
    },
    usersList: {
      display: 'flex',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
    },
    remove: {
      display: 'inline-flex',
      background: EdwardColors.LIGHT,
      padding: theme.spacing(1),
      margin: theme.spacing(0, 0.5, 0.5, 0),
      alignItems: 'center',
      borderRadius: theme.spacing(1),
      '& > span': {
        display: 'inline-flex',
        background: EdwardColors.MEDIUM,
        height: '100%',
        width: '22px',
        padding: theme.spacing(0.5),
        margin: theme.spacing(-0.5, -0.5, -0.5, 1),
        textAlign: 'center',
        borderRadius: theme.spacing(1),
        '& > svg': {
          color: EdwardColors.GRAY_DARKER,
        },
        '&:hover': {
          cursor: 'pointer',
          background: EdwardColors.GRAY_DARKER,
          '& > svg': { color: '#fff' },
        },
      },
    },
    usersTitle: {
      '& .MuiTypography-h4': {
        paddingTop: theme.spacing(1),
      },
    },
  }),
);

const UserPortfolios: React.FC<Stores> = ({ rootStore }) => {
  const { t } = useTranslation();
  const [store] = useState(new AdminUserPortfoliosStore(rootStore));
  const { fetchList } = store;

  const [portfolioCode, setPortfolioCode] = useState('');
  const [users, setUsers] = useState('');
  const [newUser, setNewUser] = useState<{ [key: string]: string }>({});
  const classes = useStyles();

  useEffect(() => {
    fetchList();
  }, [fetchList, store]);

  return (
    <>
      <Container maxWidth="md">
        <Background inside>
          {store.list.length === 0 && <Typography variant="h3">{t('admin_portfolios_noneImported')}</Typography>}
          {store.list.map((p) => (
            <Grid container className={classes.portfolio} key={p.code}>
              <Grid item xs={3}>
                <Typography variant="h3">
                  {t(`CUS_imported`)} {p.key || p.code} ({p.currency}
                  {p.code.length < 3 ? ', ' + t(`TYPE_CUS_${p.code}`) : ''})
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  name={p.code}
                  value={newUser[p.code] || ''}
                  onChange={(e) => setNewUser(merge({}, newUser, { [p.code]: e.target.value }))}
                />
              </Grid>
              <Grid item xs={3}>
                <Button
                  variant="contained"
                  color="primary"
                  id="new-account-button"
                  onClick={async () => {
                    await store.addUser(p.code, newUser[p.code]);
                    setNewUser(merge({}, newUser, { [p.code]: '' }));
                  }}
                  startIcon={<AddIcon fontSize="medium" />}
                  disabled={portfolioCode !== '' || !newUser[p.code]}
                >
                  {t('general_addUser')}
                </Button>
              </Grid>
              <Grid item xs={3}>
                {portfolioCode === p.code ? (
                  <>
                    <Button
                      variant="contained"
                      color="default"
                      onClick={() => {
                        setPortfolioCode('');
                        setUsers('');
                      }}
                    >
                      {t('general_cancel')}
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={async () => {
                        await store.editUsers(portfolioCode, users);
                        setPortfolioCode('');
                        setUsers('');
                      }}
                    >
                      {t('general_save')}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      setPortfolioCode(p.code);
                      setUsers((p.users || []).join(', '));
                    }}
                    disabled={portfolioCode !== ''}
                  >
                    {t('general_editList')}
                  </Button>
                )}
              </Grid>
              <Grid item xs={3} className={classes.usersTitle}>
                <Typography variant="h4">{t('general_usersList')}:</Typography>
              </Grid>
              <Grid item xs={9}>
                {p.code === portfolioCode ? (
                  <TextField value={users} multiline minRows={5} onChange={(e) => setUsers(e.target.value)} />
                ) : (
                  <div className={classes.usersList}>
                    {p.users?.map((user) => (
                      <span key={user} className={classes.remove}>
                        {user}
                        <span
                          onClick={async (e) => {
                            await store.removeUser(p.code, user);
                            e.stopPropagation();
                          }}
                        >
                          <Close fontSize="inherit" />
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </Grid>
            </Grid>
          ))}
        </Background>
      </Container>
    </>
  );
};

export default compose(inject('rootStore'), observer)(UserPortfolios);
